import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, zIndex as Z, shadow, radius } from '@/shared/theme/tokens';

const ToastContext = createContext(null);

const ICONS = {
  success: { icon: 'check_circle', color: semantic.success,  bg: semantic.successBg,  border: semantic.successBorder },
  error:   { icon: 'error',        color: semantic.error,    bg: semantic.errorContainer, border: semantic.errorBorder },
  info:    { icon: 'info',         color: semantic.info,     bg: semantic.infoBg,     border: semantic.infoBorder },
  warning: { icon: 'warning',      color: semantic.warning,  bg: semantic.warningBg,  border: semantic.warningBorder },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id]);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(({ message, type = 'success', duration = 5000, undoFn = null }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev, { id, message, type, undoFn }]);
    if (duration !== null) timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/**
 * Returns a function that accepts an axios error (or any Error) and shows
 * a persistent error toast (no auto-dismiss, manual close only).
 *
 * Usage:
 *   const toastApiError = useApiErrorToast();
 *   try { await mutateAsync(...) } catch (err) { toastApiError(err); }
 */
export function useApiErrorToast() {
  const toast = useToast();
  return useCallback((err) => {
    const data = err?.response?.data;
    const message =
      data?.message ??
      (Array.isArray(data?.errors) && data.errors.length
        ? data.errors.slice(0, 3).map((e) => e.message ?? JSON.stringify(e)).join('\n')
        : null) ??
      err?.message ??
      'An unexpected error occurred.';
    toast({ message, type: 'error', duration: null });
  }, [toast]);
}

function ToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px',
      zIndex: Z.toast,
      display: 'flex', flexDirection: 'column', gap: '8px',
      alignItems: 'flex-end',
      pointerEvents: 'none',
    }}>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const c = useThemeColors();
  const meta = ICONS[toast.type] ?? ICONS.success;

  return (
    <div
      style={{
        pointerEvents: 'all',
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 14px',
        background: c.bg,
        border: `1px solid ${meta.border}`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: radius.lg,
        boxShadow: shadow.toast,
        fontFamily: typography.fontFamily.body,
        fontSize: typography.fontSize.md,
        color: c.textPrimary,
        maxWidth: '340px',
        minWidth: '220px',
        animation: 'toastIn 0.22s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(24px) scale(0.97); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 18, color: meta.color, flexShrink: 0, fontVariationSettings: "'FILL' 1" }}
      >
        {meta.icon}
      </span>
      <span style={{ flex: 1, lineHeight: 1.4, fontWeight: typography.fontWeight.medium, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{toast.message}</span>
      {toast.undoFn && (
        <button
          onClick={() => { toast.undoFn(); onDismiss(toast.id); }}
          style={{
            padding: '3px 10px', borderRadius: radius.sm,
            border: `1px solid ${meta.border}`,
            background: meta.bg, color: meta.color,
            fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold,
            cursor: 'pointer', fontFamily: typography.fontFamily.body,
            flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          Undo
        </button>
      )}
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          width: 20, height: 20, borderRadius: radius.sm,
          border: 'none', background: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, flexShrink: 0, color: c.textSubtle,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>close</span>
      </button>
    </div>
  );
}
